// src/components/admin/GestionAseguradoras.js
import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import {
    getAseguradoras,
    crearAseguradora,
    actualizarAseguradora,
    eliminarAseguradora,
    inactivarAseguradora,
    reactivarAseguradora
} from '../../services/art/aseguradoraService';
import ModalConfirmacionCambioEstado from '../ModalConfirmacionCambioEstado';
import MenuAccionesFila from './MenuAccionesFila';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import '../../style/components/GestionDatosMaestros.css';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../mobile/BottomSheet';
import BotonFiltros from '../mobile/BotonFiltros';
import ChipsFiltros from '../mobile/ChipsFiltros';
import { coincideBusqueda } from '../../utils/textoComparable';

const GestionAseguradoras = () => {
    const [aseguradoras, setAseguradoras] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedAseguradora, setSelectedAseguradora] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        activo: true
    });
    const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

    const [showModalEliminar, setShowModalEliminar] = useState(false);
    const [aseguradoraAEliminar, setAseguradoraAEliminar] = useState(null);
    const [eliminando, setEliminando] = useState(false);

    const [filterNombre, setFilterNombre] = useState('');
    const [filterEstado, setFilterEstado] = useState('TODOS');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    useEffect(() => {
        cargarAseguradoras();
    }, []);

    const cargarAseguradoras = async () => {
        setLoading(true);
        try {
            const data = await getAseguradoras();
            setAseguradoras(data || []);
        } catch (error) {
            mostrarAlerta('Error al cargar aseguradoras', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const mostrarAlerta = (message, variant) => {
        setAlert({ show: true, message, variant });
        setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
    };

    const handleOpenModal = (mode, aseguradora = null) => {
        setModalMode(mode);

        if (mode === 'edit' && aseguradora) {
            setSelectedAseguradora(aseguradora);
            setFormData({
                nombre: aseguradora.nombre || '',
                activo: aseguradora.activo !== false
            });
        } else {
            setSelectedAseguradora(null);
            setFormData({ nombre: '', activo: true });
        }

        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedAseguradora(null);
        setFormData({ nombre: '', activo: true });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (modalMode === 'create') {
                await crearAseguradora(formData);
                mostrarAlerta('✅ Aseguradora creada exitosamente', 'success');
            } else {
                await actualizarAseguradora(selectedAseguradora.idAseguradora, formData);
                mostrarAlerta('✅ Aseguradora actualizada exitosamente', 'success');
            }

            handleCloseModal();
            await cargarAseguradoras();
        } catch (error) {
            mostrarAlerta('❌ Error al guardar la aseguradora', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleEstado = async (aseguradora) => {
        setLoading(true);
        try {
            if (aseguradora.activo) {
                await inactivarAseguradora(aseguradora.idAseguradora);
                mostrarAlerta('✅ Aseguradora inactivada exitosamente', 'success');
            } else {
                await reactivarAseguradora(aseguradora.idAseguradora);
                mostrarAlerta('✅ Aseguradora reactivada exitosamente', 'success');
            }
            await cargarAseguradoras();
        } catch (error) {
            mostrarAlerta('❌ Error al cambiar estado de la aseguradora', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id) => {
        const aseguradora = aseguradoras.find((a) => a.idAseguradora === id);
        setAseguradoraAEliminar(aseguradora);
        setShowModalEliminar(true);
    };

    const confirmarEliminar = async () => {
        if (!aseguradoraAEliminar) return;

        setEliminando(true);
        setLoading(true);

        try {
            await eliminarAseguradora(aseguradoraAEliminar.idAseguradora);
            mostrarAlerta('✅ Aseguradora eliminada exitosamente', 'success');
            setShowModalEliminar(false);
            setAseguradoraAEliminar(null);
            await cargarAseguradoras();
        } catch (error) {
            mostrarAlerta('❌ Error al eliminar la aseguradora', 'danger');
            setShowModalEliminar(false);
            setAseguradoraAEliminar(null);
        } finally {
            setEliminando(false);
            setLoading(false);
        }
    };

    const cancelarEliminar = () => {
        setShowModalEliminar(false);
        setAseguradoraAEliminar(null);
    };

    const getAseguradorasFiltradas = () => {
        let resultado = [...aseguradoras];

        if (filterNombre.trim()) {
            resultado = resultado.filter((a) => coincideBusqueda(a.nombre, filterNombre));
        }

        if (filterEstado !== 'TODOS') {
            const activo = filterEstado === 'ACTIVAS';
            resultado = resultado.filter((a) => a.activo === activo);
        }

        return resultado;
    };

    return (
        <div className="gestion-datos-maestros">
            {alert.show && (
                <Alert
                    variant={alert.variant}
                    dismissible
                    onClose={() => setAlert({ show: false, message: '', variant: '' })}
                >
                    {alert.message}
                </Alert>
            )}

            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex flex-wrap gap-2">
                    <Button
                        variant="outline-secondary"
                        onClick={cargarAseguradoras}
                        disabled={loading}
                        className="shadow-sm"
                        title="Recargar lista"
                    >
                        <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
                    </Button>

                    <Button
                        variant="primary"
                        onClick={() => handleOpenModal('create')}
                        className="shadow-sm"
                    >
                        <i className="bi bi-plus-circle me-2"></i>
                        Nueva Aseguradora
                    </Button>
                </div>
            </div>

            {/* Filtros */}
            {(() => {
                const limpiarFiltros = () => { setFilterNombre(''); setFilterEstado('TODOS'); };
                const chipsFiltros = [
                    filterNombre.trim() !== '' && { key: 'nombre', label: filterNombre, onQuitar: () => setFilterNombre('') },
                    filterEstado !== 'TODOS' && { key: 'estado', label: filterEstado === 'ACTIVAS' ? 'Activas' : 'Inactivas', onQuitar: () => setFilterEstado('TODOS') },
                ].filter(Boolean);
                const resumen = `Mostrando ${getAseguradorasFiltradas().length} de ${aseguradoras.length} aseguradoras`;

                const campoBuscar = (
                    <CampoFiltro etiqueta="Buscar" icono="bi-search" htmlFor="filtro-aseguradora-nombre" ancho>
                        <Form.Control
                            id="filtro-aseguradora-nombre"
                            className="panel-filtros-input"
                            type="text"
                            placeholder="Nombre de la aseguradora (ej: Provincia ART)…"
                            value={filterNombre}
                            onChange={(e) => setFilterNombre(e.target.value)}
                        />
                    </CampoFiltro>
                );
                const campoEstado = (
                    <CampoFiltro etiqueta="Estado" icono="bi-toggle-on" htmlFor="filtro-aseguradora-estado">
                        <Form.Select
                            id="filtro-aseguradora-estado"
                            className="panel-filtros-input"
                            value={filterEstado}
                            onChange={(e) => setFilterEstado(e.target.value)}
                        >
                            <option value="TODOS">Todas</option>
                            <option value="ACTIVAS">Activas</option>
                            <option value="INACTIVAS">Inactivas</option>
                        </Form.Select>
                    </CampoFiltro>
                );

                return esMobile ? (
                    <div className="mb-3">
                        <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                        <ChipsFiltros chips={chipsFiltros} />
                        {sheetFiltrosAbierto && (
                            <BottomSheet
                                titulo="Filtros"
                                onCerrar={() => setSheetFiltrosAbierto(false)}
                                accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                            >
                                {campoBuscar}
                                {campoEstado}
                            </BottomSheet>
                        )}
                        <small className="d-block mt-2 text-muted">{resumen}</small>
                    </div>
                ) : (
                    <PanelFiltros hayFiltros={chipsFiltros.length > 0} onLimpiar={limpiarFiltros} resumen={resumen}>
                        {campoBuscar}
                        {campoEstado}
                    </PanelFiltros>
                );
            })()}

            {loading && aseguradoras.length === 0 ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="datos-maestros-table">
                        <thead>
                            <tr>
                                <th>Aseguradora</th>
                                <th>Estado</th>
                                <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {getAseguradorasFiltradas().length === 0 ? (
                                <tr>
                                    <td colSpan="3" className="text-center text-muted py-4">
                                        {aseguradoras.length === 0
                                            ? 'No hay aseguradoras registradas'
                                            : 'No se encontraron aseguradoras con los filtros aplicados'}
                                    </td>
                                </tr>
                            ) : (
                                getAseguradorasFiltradas().map((aseguradora) => (
                                    <tr key={aseguradora.idAseguradora}>
                                        <td className="fw-semibold">{aseguradora.nombre}</td>
                                        <td>
                                            <span className={`badge bg-${aseguradora.activo ? 'success' : 'secondary'}`}>
                                                {aseguradora.activo ? 'Activa' : 'Inactiva'}
                                            </span>
                                        </td>
                                        <td className="text-end">
                                            <MenuAccionesFila acciones={[
                                                { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleOpenModal('edit', aseguradora) },
                                                aseguradora.activo
                                                    ? { etiqueta: 'Inactivar', icono: 'bi-pause-circle', onClick: () => handleToggleEstado(aseguradora) }
                                                    : { etiqueta: 'Reactivar', icono: 'bi-play-circle', onClick: () => handleToggleEstado(aseguradora) },
                                                { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleDelete(aseguradora.idAseguradora) },
                                            ]} />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Nueva Aseguradora' : 'Editar Aseguradora'}
                    </Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.nombre}
                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                required
                                placeholder="Ej: Provincia ART"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Check
                                type="switch"
                                id="aseguradora-activa"
                                label="Aseguradora activa"
                                checked={formData.activo}
                                onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                            />
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <ModalConfirmacionCambioEstado
                isOpen={showModalEliminar}
                onClose={cancelarEliminar}
                onConfirm={confirmarEliminar}
                titulo="Eliminar Aseguradora"
                mensaje={
                    aseguradoraAEliminar
                        ? `¿Esta seguro de eliminar la aseguradora "${aseguradoraAEliminar.nombre}"?\n\nEsta accion no se puede deshacer.`
                        : '¿Esta seguro de que desea eliminar esta aseguradora?'
                }
                cargando={eliminando}
                textoBotonConfirmar="Eliminar"
                textoBotonCancelar="Cancelar"
                variantBotonConfirmar="danger"
            />
        </div>
    );
};

export default GestionAseguradoras;

